import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { AppDispatch } from "app/store";
import { parentsFirebase } from "app/Auth";
import { actions as configActions } from "app/configSlice";
import { btnDelay, usePlaySound } from "common/utils";
import parentI18n from "features/parent/i18n";
import { updateLastSignInTime } from "features/user/api";
import { useSignUp } from "./hook";
import { MobileInput } from "./AuthStyles";

export const EmailLoginForm = ({
  onSuccess,
  onForgotPassword,
  onLoadingChange,
  showSuccessToast = false,
  keepLoadingOnSuccess = false,
}: {
  onSuccess?: () => void | Promise<void>;
  onForgotPassword?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  showSuccessToast?: boolean;
  keepLoadingOnSuccess?: boolean;
}) => {
  useSignUp();
  const dispatch = useDispatch<AppDispatch>();
  const playSound = usePlaySound();
  const auth = getAuth(parentsFirebase);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const setLoading = (loading: boolean) => {
    setIsLoading(loading);
    onLoadingChange?.(loading);
  };

  const handleLogin = async () => {
    if (!password) {
      setError(parentI18n.t("MSG_SIGN_PASSWORD_REQUIRED"));
      playSound();
      return;
    }

    setLoading(true);
    setError("");
    playSound();

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      updateLastSignInTime();
      dispatch(configActions.setJustAuthenticated(true));
      if (showSuccessToast) {
        toast(
          <div>
            <p className="text text-textcolor-black">
              {parentI18n.t("MSG_SIGN_IN_SUCCESS_CONTENT")}
            </p>
          </div>,
        );
      }
      await onSuccess?.();
      // ダイアログ版（keepLoadingOnSuccess）は認証成功後もダイアログが閉じる
      // まで AuthProvider がローディングを担うため、ここでは解除しない。
      if (!keepLoadingOnSuccess) {
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setError(parentI18n.t("MSG_SIGN_LOGIN_FAILED"));
      playSound();
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    if (!onForgotPassword) return;
    btnDelay(() => {
      playSound();
      onForgotPassword();
    });
  };

  return (
    <div className="flex-col-el flex-center w-full gap-4">
      <MobileInput
        type="email"
        value={email}
        onChange={(e) => {
          setError("");
          setEmail(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
        label={parentI18n.t("MSG_SIGN_USER_MAIL")}
        disabled={isLoading}
      />

      <MobileInput
        type="password"
        value={password}
        onChange={(e) => {
          setError("");
          setPassword(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
        label={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
        disabled={isLoading}
      />

      {error && <p className="type-text type-sm text-danger text-center">{error}</p>}

      <button
        type="button"
        className="w-[375px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
        onClick={handleLogin}
        disabled={isLoading}
      >
        {parentI18n.t("MSG_SIGN_IN_BTN")}
      </button>

      {onForgotPassword && (
        <div className="flex-row-el flex-center mt-2">
          <button
            type="button"
            className="text-blue-100 hover:text-blue-800 underline cursor-pointer bg-transparent border-none type-inherit-font type-text type-sm"
            onClick={handleForgotPassword}
          >
            {parentI18n.t("MSG_SIGN_IN_FORGOT_PASSWORD")}
          </button>
        </div>
      )}
    </div>
  );
};
